import { useState, useMemo } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Clock, Edit, AlertTriangle, FileDown, Calendar, Users, CreditCard, CheckCircle2, XCircle } from "lucide-react";
import { format, startOfDay, endOfDay, subDays } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";

interface AttendanceEvent {
  id: string;
  user_id: string;
  event_type: 'clock_in' | 'clock_out' | 'break_start' | 'break_end';
  event_at: string;
  source: string;
  notes: string | null;
  is_corrected: boolean;
}

interface UserBasic {
  id: string;
  email: string;
  full_name: string | null;
}

const eventLabel = (t: string) => ({
  clock_in: "Entrada",
  clock_out: "Salida",
  break_start: "Pausa inicio",
  break_end: "Pausa fin",
}[t] || t);

const eventColor = (t: string) => ({
  clock_in: "bg-green-500",
  clock_out: "bg-red-500",
  break_start: "bg-amber-500",
  break_end: "bg-blue-500",
}[t] || "bg-gray-500");

export default function FichajeAdmin() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 7), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedUserId, setSelectedUserId] = useState<string>("__all__");
  const [editingEvent, setEditingEvent] = useState<AttendanceEvent | null>(null);
  const [insertDialogOpen, setInsertDialogOpen] = useState(false);
  const [correctionForm, setCorrectionForm] = useState({
    event_type: "clock_in" as AttendanceEvent['event_type'],
    event_at: "",
    user_id: "",
    reason: "",
  });

  // Users (from profiles table)
  const { data: users = [] } = useQuery<UserBasic[]>({
    queryKey: ["users-list-attendance"],
    queryFn: async () => {
      const { data, error } = await api
        .from("profiles")
        .select("id, full_name, employee_id")
        .eq("is_active", true)
        .order("full_name");
      if (error) throw error;
      return (data || []).map((p: any) => ({
        id: p.id,
        email: p.employee_id || p.id.slice(0, 8),
        full_name: p.full_name,
      })) as UserBasic[];
    },
  });

  // NFC cards assigned for attendance
  const { data: attendanceCards = [] } = useQuery({
    queryKey: ["attendance-nfc-cards"],
    queryFn: async () => {
      const { data, error } = await api
        .from("nfc_tags")
        .select(`
          id,
          tag_uid,
          tag_label,
          status,
          user_profile:assigned_to_user_id (
            id,
            full_name,
            employee_id
          )
        `)
        .not("assigned_to_user_id", "is", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const userMap = useMemo(() => {
    const m: Record<string, UserBasic> = {};
    for (const u of users) m[u.id] = u;
    return m;
  }, [users]);

  // Events in range
  const fromIso = useMemo(() => startOfDay(new Date(dateFrom)).toISOString(), [dateFrom]);
  const toIso = useMemo(() => endOfDay(new Date(dateTo)).toISOString(), [dateTo]);

  const { data: events = [], isLoading } = useQuery<AttendanceEvent[]>({
    queryKey: ["attendance-admin", fromIso, toIso, selectedUserId],
    queryFn: async () => {
      let q = api
        .from("attendance_events")
        .select("*")
        .gte("event_at", fromIso)
        .lte("event_at", toIso)
        .order("event_at", { ascending: false });
      if (selectedUserId !== "__all__") q = q.eq("user_id", selectedUserId);
      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as AttendanceEvent[];
    },
  });

  // Daily summary per user
  const dailySummary = useMemo(() => {
    type Bucket = { date: string; userId: string; userName: string; minutes: number; events: AttendanceEvent[] };
    const buckets: Record<string, Bucket> = {};
    const sorted = [...events].sort((a, b) => a.event_at.localeCompare(b.event_at));
    // Group by user + date
    const byUserDate: Record<string, AttendanceEvent[]> = {};
    for (const e of sorted) {
      if (e.is_corrected) continue;
      const d = format(new Date(e.event_at), "yyyy-MM-dd");
      const k = `${e.user_id}|${d}`;
      (byUserDate[k] = byUserDate[k] || []).push(e);
    }
    for (const [k, evs] of Object.entries(byUserDate)) {
      const [userId, date] = k.split("|");
      let workMs = 0, lastIn: number | null = null, breakMs = 0, breakStart: number | null = null;
      for (const e of evs) {
        const ts = new Date(e.event_at).getTime();
        if (e.event_type === "clock_in") lastIn = ts;
        else if (e.event_type === "clock_out" && lastIn) {
          workMs += ts - lastIn - breakMs;
          lastIn = null;
          breakMs = 0;
        }
        else if (e.event_type === "break_start") breakStart = ts;
        else if (e.event_type === "break_end" && breakStart) {
          breakMs += ts - breakStart;
          breakStart = null;
        }
      }
      buckets[k] = {
        date,
        userId,
        userName: userMap[userId]?.full_name || userMap[userId]?.email || userId.slice(0, 8),
        minutes: Math.floor(workMs / 60000),
        events: evs,
      };
    }
    return Object.values(buckets).sort((a, b) => b.date.localeCompare(a.date) || a.userName.localeCompare(b.userName));
  }, [events, userMap]);

  // Mutation: correct (modify or invalidate) an existing event
  const correctMutation = useMutation({
    mutationFn: async (params: {
      event: AttendanceEvent;
      newAt?: string;
      newType?: AttendanceEvent['event_type'];
      invalidate?: boolean;
      reason: string;
    }) => {
      if (!user?.id) throw new Error("No autenticado");
      const { event, newAt, newType, invalidate, reason } = params;
      // Insert correction record (audit)
      const { error: corrErr } = await api.from("attendance_corrections").insert({
        event_id: event.id,
        user_id: event.user_id,
        action: invalidate ? "invalidate" : "modify",
        original_event_at: event.event_at,
        new_event_at: invalidate ? null : (newAt || event.event_at),
        original_event_type: event.event_type,
        new_event_type: invalidate ? null : (newType || event.event_type),
        reason,
        corrected_by: user.id,
      });
      if (corrErr) throw corrErr;
      if (invalidate) {
        // Mark as corrected (effectively excluded from totals)
        const { error } = await api.from("attendance_events").update({ is_corrected: true }).eq("id", event.id);
        if (error) throw error;
      } else {
        const { error } = await api.from("attendance_events").update({
          event_at: newAt || event.event_at,
          event_type: newType || event.event_type,
          is_corrected: false,
        }).eq("id", event.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success("Corrección registrada en audit trail");
      queryClient.invalidateQueries({ queryKey: ["attendance-admin"] });
      setEditingEvent(null);
    },
    onError: (e: any) => toast.error(`Error: ${e.message}`),
  });

  // Mutation: insert manual event (for cases like "olvidó fichar entrada")
  const insertMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("No autenticado");
      if (!correctionForm.user_id || !correctionForm.event_at || !correctionForm.reason) {
        throw new Error("Faltan campos obligatorios");
      }
      // Insert event with source=manual
      const { data: inserted, error: insErr } = await api.from("attendance_events").insert({
        user_id: correctionForm.user_id,
        event_type: correctionForm.event_type,
        event_at: new Date(correctionForm.event_at).toISOString(),
        source: "manual",
        notes: `Insertado manualmente por admin. Motivo: ${correctionForm.reason}`,
      }).select("id").single();
      if (insErr) throw insErr;
      // Audit: insert correction record
      const { error: corrErr } = await api.from("attendance_corrections").insert({
        event_id: (inserted as any)?.id,
        user_id: correctionForm.user_id,
        action: "insert",
        original_event_at: null,
        new_event_at: new Date(correctionForm.event_at).toISOString(),
        original_event_type: null,
        new_event_type: correctionForm.event_type,
        reason: correctionForm.reason,
        corrected_by: user.id,
      });
      if (corrErr) throw corrErr;
    },
    onSuccess: () => {
      toast.success("Evento insertado y registrado en audit trail");
      queryClient.invalidateQueries({ queryKey: ["attendance-admin"] });
      setInsertDialogOpen(false);
      setCorrectionForm({ event_type: "clock_in", event_at: "", user_id: "", reason: "" });
    },
    onError: (e: any) => toast.error(`Error: ${e.message}`),
  });

  // CSV Export
  const exportCsv = () => {
    const rows = [
      ["Fecha", "Usuario", "Tipo", "Hora", "Origen", "Corregido", "Notas"],
      ...events.map((e) => [
        format(new Date(e.event_at), "yyyy-MM-dd"),
        userMap[e.user_id]?.full_name || userMap[e.user_id]?.email || e.user_id,
        eventLabel(e.event_type),
        format(new Date(e.event_at), "HH:mm:ss"),
        e.source,
        e.is_corrected ? "Sí" : "No",
        (e.notes || "").replace(/,/g, ";"),
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fichaje-${dateFrom}_${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Fichaje — Administración</h1>
            <p className="text-muted-foreground">Registros, totales y correcciones (RDL 8/2019)</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportCsv}><FileDown className="w-4 h-4 mr-2" /> Export CSV</Button>
            <Button onClick={() => setInsertDialogOpen(true)}>+ Insertar evento</Button>
          </div>
        </div>

        {/* Tarjetas NFC de Fichaje */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Tarjetas NFC de Fichaje ({attendanceCards.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {attendanceCards.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No hay tarjetas asignadas. Ve a <strong>Etiquetas NFC</strong> para asignar tarjetas a usuarios.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {attendanceCards.map((card: any) => (
                  <div
                    key={card.id}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-card"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      card.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      <CreditCard className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {card.user_profile?.full_name || card.user_profile?.employee_id || "Sin nombre"}
                      </p>
                      <p className="text-xs text-muted-foreground font-mono truncate">
                        {card.tag_label || card.tag_uid}
                      </p>
                    </div>
                    {card.status === 'active' ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Filtros */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label>Desde</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div>
                <Label>Hasta</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <Label>Trabajador</Label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos los trabajadores</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Daily Summary */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5" /> Totales diarios</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Trabajador</TableHead>
                  <TableHead>Horas trabajadas</TableHead>
                  <TableHead>Eventos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dailySummary.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">Sin datos en el rango</TableCell></TableRow>
                ) : dailySummary.map((b) => (
                  <TableRow key={`${b.date}-${b.userId}`}>
                    <TableCell>{format(new Date(b.date), "EEE dd MMM yyyy", { locale: es })}</TableCell>
                    <TableCell>{b.userName}</TableCell>
                    <TableCell className="font-mono">{Math.floor(b.minutes / 60)}h {String(b.minutes % 60).padStart(2, "0")}m</TableCell>
                    <TableCell>{b.events.length}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Eventos brutos */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" /> Eventos ({events.length})</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha/Hora</TableHead>
                  <TableHead>Trabajador</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Origen</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={6} className="text-center">Cargando...</TableCell></TableRow>
                ) : events.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Sin eventos en el rango</TableCell></TableRow>
                ) : events.map((e) => (
                  <TableRow key={e.id} className={e.is_corrected ? "opacity-50" : ""}>
                    <TableCell className="font-mono text-xs">{format(new Date(e.event_at), "dd/MM HH:mm:ss")}</TableCell>
                    <TableCell>{userMap[e.user_id]?.full_name || userMap[e.user_id]?.email || e.user_id.slice(0, 8)}</TableCell>
                    <TableCell>
                      <Badge className={`${eventColor(e.event_type)} text-white`}>{eventLabel(e.event_type)}</Badge>
                    </TableCell>
                    <TableCell><Badge variant="outline">{e.source}</Badge></TableCell>
                    <TableCell>
                      {e.is_corrected ? <Badge variant="destructive">Invalidado</Badge> : <Badge variant="outline">OK</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setEditingEvent(e)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingEvent} onOpenChange={(o) => { if (!o) setEditingEvent(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Corregir evento de fichaje</DialogTitle>
            <DialogDescription>
              Cumplimiento RDL 8/2019: toda corrección queda registrada en audit trail con motivo obligatorio.
            </DialogDescription>
          </DialogHeader>
          {editingEvent && (
            <CorrectEventForm
              event={editingEvent}
              onSubmit={(params) => correctMutation.mutate({ event: editingEvent, ...params })}
              loading={correctMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Insert manual event Dialog */}
      <Dialog open={insertDialogOpen} onOpenChange={setInsertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insertar evento manual</DialogTitle>
            <DialogDescription>
              Para casos donde el trabajador olvidó fichar. Queda registrado en audit trail con motivo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Trabajador *</Label>
              <Select value={correctionForm.user_id} onValueChange={(v) => setCorrectionForm({ ...correctionForm, user_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo *</Label>
              <Select value={correctionForm.event_type} onValueChange={(v: any) => setCorrectionForm({ ...correctionForm, event_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="clock_in">Entrada</SelectItem>
                  <SelectItem value="clock_out">Salida</SelectItem>
                  <SelectItem value="break_start">Pausa inicio</SelectItem>
                  <SelectItem value="break_end">Pausa fin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fecha y hora *</Label>
              <Input type="datetime-local" value={correctionForm.event_at} onChange={(e) => setCorrectionForm({ ...correctionForm, event_at: e.target.value })} />
            </div>
            <div>
              <Label>Motivo *</Label>
              <Textarea
                value={correctionForm.reason}
                onChange={(e) => setCorrectionForm({ ...correctionForm, reason: e.target.value })}
                placeholder="Ej: trabajador olvidó fichar entrada el lunes 28-04 a las 8:00"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInsertDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => insertMutation.mutate()} disabled={insertMutation.isPending}>Insertar evento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function CorrectEventForm({
  event,
  onSubmit,
  loading,
}: {
  event: AttendanceEvent;
  onSubmit: (params: { newAt?: string; newType?: AttendanceEvent['event_type']; invalidate?: boolean; reason: string }) => void;
  loading: boolean;
}) {
  const [mode, setMode] = useState<"modify" | "invalidate">("modify");
  const [newAt, setNewAt] = useState(format(new Date(event.event_at), "yyyy-MM-dd'T'HH:mm"));
  const [newType, setNewType] = useState<AttendanceEvent['event_type']>(event.event_type);
  const [reason, setReason] = useState("");

  return (
    <div className="space-y-4">
      <div className="bg-muted p-3 rounded text-sm space-y-1">
        <p><strong>Original:</strong> {eventLabel(event.event_type)} a {format(new Date(event.event_at), "dd/MM/yyyy HH:mm:ss")}</p>
        <p><strong>Origen:</strong> {event.source}</p>
      </div>
      <div className="flex gap-2">
        <Button variant={mode === "modify" ? "default" : "outline"} size="sm" onClick={() => setMode("modify")}>Modificar</Button>
        <Button variant={mode === "invalidate" ? "destructive" : "outline"} size="sm" onClick={() => setMode("invalidate")}>
          <AlertTriangle className="w-4 h-4 mr-1" /> Invalidar
        </Button>
      </div>
      {mode === "modify" && (
        <>
          <div>
            <Label>Nueva fecha y hora</Label>
            <Input type="datetime-local" value={newAt} onChange={(e) => setNewAt(e.target.value)} />
          </div>
          <div>
            <Label>Nuevo tipo</Label>
            <Select value={newType} onValueChange={(v: any) => setNewType(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="clock_in">Entrada</SelectItem>
                <SelectItem value="clock_out">Salida</SelectItem>
                <SelectItem value="break_start">Pausa inicio</SelectItem>
                <SelectItem value="break_end">Pausa fin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}
      <div>
        <Label>Motivo *</Label>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Razón obligatoria para auditoría legal" />
      </div>
      <Button
        className="w-full"
        disabled={loading || !reason.trim()}
        onClick={() => {
          if (mode === "invalidate") onSubmit({ invalidate: true, reason });
          else onSubmit({ newAt: new Date(newAt).toISOString(), newType, reason });
        }}
      >
        {loading ? "Guardando..." : (mode === "invalidate" ? "Confirmar invalidación" : "Confirmar modificación")}
      </Button>
    </div>
  );
}
